import React, { useMemo, useState, useEffect, useRef, Fragment } from "react";
import { ChevronUp, ChevronDown } from 'lucide-react';
import { getdata, Fruitsdetails, deleteFruitsdetails } from "./store/index";
import { useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next'
import Breadcrumbs from '../../breadcrumbs'
import img from '../../../images/no-data.svg'
import { Link, useNavigate } from "react-router-dom";
import { IoIosAddCircle } from "react-icons/io";

import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink
} from "reactstrap";
import '../ProfileQuestions/ProfileQuestions.css';
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import Swal from "sweetalert2";
import { ComponentLoader } from '../common/Loader';

const Fruits = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [viewAllData, setViewAllData] = useState([]);
  const navigate = useNavigate();

  // Filters state
  const [filters, setFilters] = useState({
      name: "",
      startDate: "",
      endDate: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lang = localStorage.getItem('lang');
  const toggleModal = () => setModalOpen(!modalOpen);
  const hasFetched = useRef(false);

  const fetchData = async () => {
    try {
      const cachedData = sessionStorage.getItem('FRUITS_CACHE');
      let cacheExists = false;

      if (cachedData) {
        const data = JSON.parse(cachedData);
        if (data === 'PENDING') {
          return;
        }

        if (data && typeof data === 'object') {
          const cacheTimestamp = data.cacheTimestamp || 0;
          const threeMinutes = 3 * 60 * 1000;
          const isExpired = Date.now() - cacheTimestamp > threeMinutes;

          if (!isExpired) {
            setAllData(data.fruits);
         //   console.log("data 1 : " ,data.fruits )
            setFilteredData(data.fruits);
            setIsLoading(false);
            return;
          } else {
            setAllData(data.fruits);
          //  console.log("data 2 : " ,data.fruits )
            setFilteredData(data.fruits);
            setIsLoading(false);
            cacheExists = true;
          }
        }
      }

      if (!cacheExists) {
        setIsLoading(true);
      }
      sessionStorage.setItem('FRUITS_CACHE', JSON.stringify('PENDING'));

      const response = await dispatch(getdata(lang));
     
      if (response.payload && response.payload.data) {
        setAllData(response.payload.data);
        // console.log("hihi fruits :", response.payload.data);
        setFilteredData(response.payload.data);
        sessionStorage.setItem('FRUITS_CACHE', JSON.stringify({
          fruits: response.payload.data,
          cacheTimestamp: Date.now()
        }));
      } else {
        sessionStorage.removeItem('FRUITS_CACHE');
      }
    } catch (error) {
      console.error("Error fetching fruits data:", error);
      sessionStorage.removeItem('FRUITS_CACHE');
    } finally {
      setIsLoading(false);
    }
  };

  // Local Filtering Logic
  useEffect(() => {
      let result = [...allData];

      if (filters.name) {
          result = result.filter(item => item.name && item.name.toLowerCase().includes(filters.name.toLowerCase()));
      }
      if (filters.startDate) {
          result = result.filter(item => {
              const recordDate = item.lastSyncDate;
              if (!recordDate) return false;
              const itemDate = new Date(recordDate).setHours(0, 0, 0, 0);
              const filterDate = new Date(filters.startDate).setHours(0, 0, 0, 0);
              return itemDate >= filterDate;
          });
      }
      if (filters.endDate) {
          result = result.filter(item => {
              const recordDate = item.lastSyncDate;
              if (!recordDate) return false;
              const itemDate = new Date(recordDate).setHours(0, 0, 0, 0);
              const filterDate = new Date(filters.endDate).setHours(23, 59, 59, 999);
              return itemDate <= filterDate;
          });
      }

      setFilteredData(result);
      setCurrentPage(1); // Reset to first page when filtering
  }, [filters, allData]);

  const handleFilterChange = (e) => {
      const { name, value } = e.target;
      setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
      setFilters({
          name: "",
          startDate: "",
          endDate: "",
      });
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [lang]);

  // Pagination Calculation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const handleSort = (field) => {
      let direction = 'asc';
      if (sortConfig.key === field && sortConfig.direction === 'asc') {
          direction = 'desc';
      }
      setSortConfig({ key: field, direction });
  };

  const sortedData = useMemo(() => {
      let sortableItems = [...filteredData];
      if (sortConfig.key) {
          sortableItems.sort((a, b) => {
              let aValue = a[sortConfig.key];
              let bValue = b[sortConfig.key];

              if (sortConfig.key === 'lastSyncDate') {
                  aValue = new Date(aValue).getTime() || 0;
                  bValue = new Date(bValue).getTime() || 0;
              } else {
                  aValue = aValue ? aValue.toString().toLowerCase() : '';
                  bValue = bValue ? bValue.toString().toLowerCase() : '';
              }

              if (aValue < bValue) {
                  return sortConfig.direction === 'asc' ? -1 : 1;
              }
              if (aValue > bValue) {
                  return sortConfig.direction === 'asc' ? 1 : -1;
              }
              return 0;
          });
      }
      return sortableItems;
  }, [filteredData, sortConfig]);

  const renderSortHeader = (label, field) => (
      <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              <span>{label}</span>
              <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', gap: '4px' }}>
                  <ChevronUp size={16} strokeWidth={3} color={sortConfig.key === field && sortConfig.direction === 'asc' ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSort(field)} />
                  <ChevronDown size={16} strokeWidth={3} color={sortConfig.key === field && sortConfig.direction === 'desc' ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSort(field)} />
              </span>
          </div>
      </th>
  );

  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (page) => {
      if (page >= 1 && page <= totalPages) {
          setCurrentPage(page);
      }
  };


  const handleView = (id) => {
    sessionStorage.removeItem('FRUITS_CACHE');
    dispatch(Fruitsdetails(id)).then((res) => {
      setViewAllData(res.payload.data)
      setModalOpen(true);
    })
  }

  const handleEdit = (id) => {
    sessionStorage.removeItem('FRUITS_CACHE');
    navigate(`/fruits/edit/${id}`)
  }

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Fruits Details',
      text: 'Are you sure want to delete this record?',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: t('OK'),
      cancelButtonText: t('Cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteFruitsdetails(id)).then((res) => {
          if (res.payload.response === 'S') {
            Swal.fire({
              icon: 'success',
              title: t('Success!'),
              text: t('Deleted Successfully'),
            });
            fetchData(); // Call fetchData after deletion to update the list
          }
        });
      }
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const dateOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const datePart = date.toLocaleDateString('en-US', dateOptions).replace(/,/g, '');

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const timePart = `${hours}.${minutes} ${ampm}`;

    return `${datePart} , ${timePart}`;
  };

  return (
    <div className="pq-list-container">
      <div className="mb-4 text-start">
        <Button className="pq-btn-add d-inline-block" style={{ width: 'auto' }} onClick={() => navigate('/fruits/add')}>
          <i className="fa fa-plus me-2"></i> {t('Add Fruits Details')}
        </Button>
      </div>

      {/* Filter Bar */}
      <div className="pq-filter-container">
        <Row className="g-3 align-items-center">
          <Col md={3}>
            <Input type="text" name="name" placeholder={t("Search by name...")} value={filters.name} onChange={handleFilterChange} className="pq-filter-input" />
          </Col>
          <Col md={3}>
            <Input 
              type={filters.startDate ? "date" : "text"} 
              name="startDate" 
              placeholder={t("Start Date")} 
              value={filters.startDate} 
              max={filters.endDate} 
              onChange={handleFilterChange} 
              onFocus={(e) => (e.target.type = "date")}
              onBlur={(e) => (!e.target.value ? (e.target.type = "text") : null)}
              className="pq-filter-input" 
            />
          </Col>
          <Col md={3}>
            <Input 
              type={filters.endDate ? "date" : "text"} 
              name="endDate" 
              placeholder={t("End Date")} 
              value={filters.endDate} 
              min={filters.startDate} 
              onChange={handleFilterChange} 
              onFocus={(e) => (e.target.type = "date")}
              onBlur={(e) => (!e.target.value ? (e.target.type = "text") : null)}
              className="pq-filter-input" 
            />
          </Col>
          <Col md={2}>
            <button type="button" onClick={handleClearFilters} className="btn w-100 global-clear-btn-black-text" title={t("Clear Filters")}>
              Clear
            </button>
          </Col>
        </Row>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <ComponentLoader color="#8b5cf6" size="40px" />
        </div>
      ) : filteredData.length === 0 ? (
        <div className="d-flex justify-content-center">
          <img src={img} alt="No Data" style={{ height: '50vh' }} />
        </div>
      ) : (
        <div className="pq-table-wrapper">
          <div className="pq-table-scroll">
            <table className="pq-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>{t('NO')}</th>
                  {renderSortHeader(t('NAME'), 'name')}
                  {renderSortHeader(t('DATE'), 'lastSyncDate')}
                  <th style={{ textAlign: 'center' }}>{t('ACTIONS')}</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => (
                  <tr key={index}>
                    <td style={{ textAlign: 'center' }}>{indexOfFirstItem + index + 1}</td>
                    <td>{item.name}</td>
                    <td style={{ textAlign: 'center' }}>{formatDateTime(item.lastSyncDate)}</td>
                    <td>
                      <div className="d-flex justify-content-center gap-2">
                        <button className="pq-action-btn" style={{ backgroundColor: '#28a745', color: 'white', border: 'none' }} onClick={() => handleView(item.fruitId)} title={t('View')}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="feather feather-eye"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                        <button className="pq-action-btn" onClick={() => handleEdit(item.fruitId)} title={t('Edit')}>
                          <i className="fas fa-pen"></i>
                        </button>
                        <button className="pq-action-btn pq-action-btn-danger" onClick={() => handleDelete(item.fruitId)} title={t('Delete')}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="pq-pagination-container d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <span className="pq-pagination-text me-2">Showing {filteredData.length > 0 ? indexOfFirstItem + 1 : 0} – {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries. Per page: </span>
              <Input type="select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="pq-filter-input" style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important' }}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Input>
            </div>

            <Pagination size="sm" className="mb-0">
              <PaginationItem className="pq-page-item" disabled={currentPage === 1}>
                <PaginationLink className="pq-page-link" previous onClick={() => handlePageChange(currentPage - 1)} />
              </PaginationItem>

              {[...Array(totalPages)].map((_, i) => {
                if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                  return (
                    <PaginationItem className="pq-page-item" active={i + 1 === currentPage} key={i}>
                      <PaginationLink className="pq-page-link" onClick={() => handlePageChange(i + 1)}>
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                if (i === 1 && currentPage > 3) return <PaginationItem className="pq-page-item" disabled key="start-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                if (i === totalPages - 2 && currentPage < totalPages - 2) return <PaginationItem className="pq-page-item" disabled key="end-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                return null;
              })}

              <PaginationItem className="pq-page-item" disabled={currentPage === totalPages || totalPages === 0}>
                <PaginationLink className="pq-page-link" next onClick={() => handlePageChange(currentPage + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        </div>
      )}
      {/* view Details */}
      <Modal
        isOpen={modalOpen} toggle={toggleModal} size="xl">
        <ModalHeader toggle={toggleModal}>{t("Fruits Details")}</ModalHeader>
        <ModalBody>
          <Tabs
            selectedIndex={selectedTabIndex}
            onSelect={(index) => setSelectedTabIndex(index)}
          >
            <TabList >
              {viewAllData.map((item, index) => (
                <Tab key={index}>{item.collectionName.split("_")[1].charAt(0).toUpperCase() + item.collectionName.split("_")[1].slice(1)}</Tab>

              ))}
            </TabList>
            {viewAllData.map((item, index) => (
              <TabPanel key={index}>
                <br />
                <h2>{item?.data?.name ?? ""}</h2>
                <br />
                <img src={item?.data?.img ?? ""} alt={item?.data?.name ?? ""} style={{ width: "100%" }} />
                <br />
                <div className="col">
                  <div className="text-black">
                    <div dangerouslySetInnerHTML={{ __html: item?.data?.content ?? "" }}></div>
                  </div>
                </div>
              </TabPanel>
            ))}

          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>

  );
};

export default Fruits;
